import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DirectusService } from '../core/services/directus.service';

type MemberFilter = 'all' | 'active' | 'expired';
type SortDirection = 'asc' | 'desc';
type ColumnKey = 'name' | 'email' | 'phone' | 'status' | 'joinedDate' | 'address' | 'points';

interface MemberRow {
  id: string;
  initials: string;
  avatar: string | null;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: 'Active' | 'Expired';
  verified: boolean;
  joinedDate: string;
  joinedDateRaw: string;
  subscribedUntil: string;
  point: number;
  addressRaw: string;
  kota: string;
  provinsi: string;
  address: string;
}

interface ViewPreset {
  id: string;
  name: string;
  filter: MemberFilter;
  sort: SortDirection;
  columns: Record<ColumnKey, boolean>;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section class="people-page">
      <header class="people-header">
        <h2>Members</h2>
        <p>Active and expired member records.</p>
      </header>

      <section class="table-shell">
        <div class="toolbar">
          <div class="left">
            <select [ngModel]="selectedViewId" (ngModelChange)="applyView($event)">
              <option *ngFor="let view of views" [value]="view.id">
                {{ view.name }} ({{ countForView(view.filter) }})
              </option>
            </select>
            <div class="menu-wrap">
              <button type="button" class="linkish" (click)="toggleMenu('manage')">Manage View</button>
              <div class="menu" *ngIf="openMenu === 'manage'">
                <button type="button" (click)="saveViewChanges()">Save changes</button>
                <button type="button" (click)="saveAsNewView()">Save as new view</button>
                <button type="button" (click)="renameView()">Rename</button>
                <button type="button" (click)="setDefaultView()">Set as default view</button>
                <button type="button" class="danger" (click)="deleteCurrentView()">Delete</button>
              </div>
            </div>
          </div>
          <div class="right">
            <button type="button" class="btn-outline" (click)="openSidePanel('filter')">Filter</button>
            <button type="button" class="btn-icon" (click)="openSidePanel('customize')">☷</button>
            <input type="search" [(ngModel)]="searchTerm" placeholder="Search..." />
            <div class="menu-wrap">
              <button type="button" class="btn-outline" (click)="toggleMenu('export')">Import / Export</button>
              <div class="menu" *ngIf="openMenu === 'export'">
                <button type="button" (click)="exportCsv()">Export CSV</button>
                <button type="button" (click)="exportExcel()">Export Excel</button>
              </div>
            </div>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th class="check"><input type="checkbox" /></th>
                <th *ngIf="columns.name">
                  <button type="button" class="th-sort" (click)="toggleNameSort()">Name {{ nameSort === 'asc' ? '↑' : '↓' }}</button>
                </th>
                <th *ngIf="columns.email">Email</th>
                <th *ngIf="columns.phone">Phone</th>
                <th *ngIf="columns.status">Member status</th>
                <th *ngIf="columns.joinedDate">Joined</th>
                <th *ngIf="columns.address">Address</th>
                <th *ngIf="columns.points">Points</th>
                <th class="actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of filteredRows" (click)="openProfile(row.id)" class="member-row">
                <td class="check"><input type="checkbox" (click)="$event.stopPropagation()" /></td>
                <td class="name-cell" *ngIf="columns.name">
                  <span class="name-cell-inner">
                    <span class="avatar">
                      <img *ngIf="row.avatar" [src]="row.avatar" [alt]="row.name" class="avatar-img" />
                      <span *ngIf="!row.avatar" class="avatar-initials">{{ row.initials }}</span>
                    </span>
                    <span class="name-stack">
                      <span class="name">{{ row.name }}</span>
                      <span class="name-sub" *ngIf="row.email !== '—'">{{ row.email }}</span>
                    </span>
                  </span>
                </td>
                <td *ngIf="columns.email" class="muted-cell">{{ row.email }}</td>
                <td *ngIf="columns.phone" class="muted-cell">{{ row.phone }}</td>
                <td *ngIf="columns.status">
                  <div class="status-wrap">
                    <span class="status-badge" [class.expired]="row.status === 'Expired'">
                      {{ row.status === 'Active' ? 'Active' : 'Expired' }}
                    </span>
                    <span class="verified-badge" *ngIf="row.verified">✓ Verified</span>
                  </div>
                </td>
                <td *ngIf="columns.joinedDate" class="muted-cell">{{ row.joinedDate }}</td>
                <td *ngIf="columns.address" class="muted-cell">{{ row.address }}</td>
                <td *ngIf="columns.points" class="muted-cell">{{ row.point > 0 ? row.point : '—' }}</td>
                <td class="actions"><button type="button" class="dots" (click)="$event.stopPropagation()">···</button></td>
              </tr>
              <tr *ngIf="!loading && filteredRows.length === 0">
                <td [attr.colspan]="tableColspan" class="empty">No members found.</td>
              </tr>
              <tr *ngIf="loading">
                <td [attr.colspan]="tableColspan" class="empty">Loading members...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <div class="panel-backdrop" *ngIf="sidePanel" (click)="closeSidePanel()"></div>
      <aside
        class="drawer unified-drawer"
        *ngIf="sidePanel"
        role="dialog"
        aria-modal="true"
        [style.top.px]="drawerTop"
        [style.height]="'calc(100vh - ' + drawerTop + 'px)'"
      >
        <header>
          <h3>{{ sidePanel === 'filter' ? 'Filter' : 'Customize columns' }}</h3>
          <button type="button" class="close" (click)="closeSidePanel()">×</button>
        </header>

        <section class="drawer-body" *ngIf="sidePanel === 'filter'">
          <section class="drawer-section section-plain">
            <div class="section-title">
              <h4>Member status {{ filter !== 'all' ? '(1)' : '' }}</h4>
              <span>▾</span>
            </div>
            <div class="options">
              <label class="option-line plain"><input type="radio" name="memberStatus" value="all" [(ngModel)]="filter" /> <span>All</span></label>
              <label class="option-line plain"><input type="radio" name="memberStatus" value="active" [(ngModel)]="filter" /> <span>Active</span></label>
              <label class="option-line plain"><input type="radio" name="memberStatus" value="expired" [(ngModel)]="filter" /> <span>Expired</span></label>
            </div>
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>First name contains</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="text" [(ngModel)]="firstNameFilter" placeholder="Andre" />
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>Last name contains</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="text" [(ngModel)]="lastNameFilter" placeholder="Yanuarsia" />
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>Email contains</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="text" [(ngModel)]="emailFilter" placeholder="example@gmail.com" />
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>Phone contains</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="text" [(ngModel)]="phoneFilter" placeholder="+62..." />
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>Address contains</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="text" [(ngModel)]="addressFilter" placeholder="Jl. Sudirman" />
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>Kota (City) contains</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="text" [(ngModel)]="kotaFilter" placeholder="Jakarta" />
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>Provinsi contains</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="text" [(ngModel)]="provinsiFilter" placeholder="DKI Jakarta" />
          </section>

          <section class="drawer-section section-plain filter-input-section">
            <div class="section-title">
              <h4>Joined date</h4>
              <span>▾</span>
            </div>
            <input class="compact-input" type="date" [(ngModel)]="joinedDateFilter" />
          </section>
        </section>

        <section class="drawer-body" *ngIf="sidePanel === 'customize'">
          <div class="manage-view in-panel">
            <label *ngFor="let col of columnKeys">
              <input type="checkbox" [checked]="columns[col]" (change)="toggleColumn(col)" />
              {{ columnLabel(col) }}
            </label>
          </div>
        </section>

        <footer class="filter-footer" *ngIf="sidePanel === 'filter'">
          <span *ngIf="sidePanel === 'filter'">{{ activeFilterCount }} filter applied</span>
          <button type="button" (click)="clearFilters()">Clear</button>
        </footer>
      </aside>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .people-page {
      display: grid;
      gap: 12px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .people-header h2 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      letter-spacing: -0.02em;
      font-weight: 700;
      color: var(--app-text);
    }

    .people-header p {
      margin: 0.35rem 0 0;
      color: var(--app-muted);
      font-size: var(--type-body);
    }

    .table-shell {
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      border-radius: 12px;
      overflow: hidden;
    }

    .toolbar {
      padding: 0.8rem 1rem;
      display: flex;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid var(--app-border);
    }

    .left,
    .right {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    select,
    input[type='search'] {
      border: 1px solid #cbd5e1;
      background: var(--jci-white);
      color: var(--app-text);
      border-radius: 999px;
      padding: 0.4rem 0.7rem;
      font-size: var(--type-body);
    }

    input[type='search'] {
      min-width: 220px;
    }

    .linkish {
      border: 0;
      background: transparent;
      color: var(--jci-blue);
      font-weight: 500;
      cursor: pointer;
    }

    .btn-outline {
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
      font-weight: 600;
      padding: 0.4rem 0.8rem;
    }

    .btn-icon {
      width: 34px;
      height: 34px;
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: #f4fbff;
      color: var(--jci-blue);
    }

    .table-wrap {
      overflow-x: auto;
    }

    .manage-view {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      padding: 0.65rem 1rem;
      border-bottom: 1px solid var(--app-border);
      background: #f8fbff;
    }

    .manage-view label {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      color: var(--app-text);
      font-size: 0.88rem;
    }

    .manage-view.in-panel {
      border: 0;
      background: transparent;
      padding: 0;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }

    .menu-wrap {
      position: relative;
    }

    .menu {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      min-width: 180px;
      border: 1px solid var(--app-border);
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
      z-index: 20;
      padding: 6px;
      display: grid;
      gap: 4px;
    }

    .menu button {
      border: 0;
      background: transparent;
      text-align: left;
      padding: 0.45rem 0.55rem;
      border-radius: 6px;
      cursor: pointer;
    }

    .menu button:hover {
      background: #f4fbff;
    }

    .menu button.danger {
      color: #b91c1c;
    }

    .panel-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.35);
      z-index: 90;
    }

    .drawer {
      position: fixed;
      right: 0;
      width: var(--filter-drawer-width);
      background: #ffffff;
      border-left: 1px solid #dbe3ee;
      border-top: 1px solid #dbe3ee;
      border-radius: 0;
      z-index: 95;
      overflow: hidden;
      display: grid;
      grid-template-rows: auto 1fr auto;
      box-shadow: 0 8px 22px rgba(15, 23, 42, 0.16);
    }

    .drawer header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      align-items: flex-start;
      padding: 0.72rem 0.78rem 0.62rem;
      border-bottom: 1px solid #e2e8f0;
      background: #ffffff;
    }

    .drawer h3 {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 700;
    }

    .drawer-body {
      overflow-y: auto;
      padding: 0;
      display: grid;
      gap: 0;
      align-content: start;
      grid-auto-rows: max-content;
    }

    .drawer .close {
      border: 0;
      background: transparent;
      font-size: 1.5rem;
      cursor: pointer;
      color: #334155;
    }

    .drawer-section {
      padding: 0.72rem 0.78rem;
      display: grid;
      gap: 0.34rem;
      border-bottom: 1px solid #e2e8f0;
    }

    .filter-input-section {
      padding-top: 0.58rem;
      padding-bottom: 0.58rem;
      gap: 0.22rem;
    }

    .section-plain {
      background: #ffffff;
    }

    .section-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
    }

    .section-title h4 {
      margin: 0;
      font-size: 1rem;
      line-height: 1.25;
      font-weight: 700;
      color: #0f172a;
    }

    .section-title span {
      color: #334155;
      font-size: 0.9rem;
    }

    .options {
      display: grid;
      gap: 0.02rem;
    }

    .option-line {
      border: 0;
      border-radius: 0;
      padding: 0.02rem 0;
      background: #ffffff;
      min-height: auto;
    }

    .option-line.plain {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      justify-content: flex-start;
    }

    .option-line input {
      margin: 0;
    }

    .option-line span {
      color: #1e293b;
      font-weight: 400;
      line-height: 1.15;
      font-size: 1rem;
    }

    .compact-input {
      border: 1px solid #cbd5e1;
      border-radius: 999px;
      padding: 0.2rem 0.56rem;
      width: 100%;
      background: #ffffff;
      font-size: 0.96rem;
      height: 36px;
      min-height: 36px;
      line-height: 1.25;
      box-sizing: border-box;
    }

    .filter-footer {
      border-top: 1px solid #e2e8f0;
      padding: 0.7rem 0.78rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.82rem;
      font-weight: 600;
      background: #ffffff;
    }

    .filter-footer button {
      border: 0;
      background: transparent;
      color: #0097d7;
      cursor: pointer;
      font-weight: 600;
    }

    .th-sort {
      border: 0;
      background: transparent;
      font: inherit;
      color: inherit;
      cursor: pointer;
      padding: 0;
      font-weight: 700;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      min-width: 980px;
    }

    thead tr {
      background: #f0f7fc;
    }

    th {
      padding: 0.55rem 0.75rem;
      border-bottom: 2px solid #d4e9f5;
      text-align: left;
      font-size: 0.8rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #64748b;
    }

    td {
      padding: 0.75rem 0.75rem;
      border-bottom: 1px solid var(--app-border);
      text-align: left;
      font-size: 0.9rem;
      color: var(--app-text);
      vertical-align: middle;
    }

    .member-row {
      cursor: pointer;
      transition: background 0.1s;
    }

    .member-row:hover {
      background: #f8fcff;
    }

    .member-row:hover .dots {
      opacity: 1;
    }

    th.check,
    td.check {
      width: 34px;
      text-align: center;
    }

    th.actions,
    td.actions {
      width: 56px;
      text-align: right;
    }

    .muted-cell {
      color: #64748b;
      font-size: 0.88rem;
    }

    .name-cell {
      min-width: 240px;
    }

    .name-cell-inner {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 999px;
      display: inline-grid;
      place-items: center;
      flex: 0 0 auto;
      overflow: hidden;
      background: var(--jci-blue);
    }

    .avatar-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 999px;
    }

    .avatar-initials {
      font-size: 0.78rem;
      font-weight: 700;
      color: #fff;
    }

    .name-stack {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .name {
      font-weight: 600;
      font-size: 0.92rem;
      color: #0f172a;
    }

    .name-sub {
      font-size: 0.78rem;
      color: #94a3b8;
      font-weight: 400;
    }

    .status-wrap {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .status-badge {
      display: inline-block;
      border: 1px solid #8dd2a6;
      color: #167a3d;
      background: #ecfff2;
      font-size: 0.72rem;
      font-weight: 700;
      border-radius: 4px;
      padding: 0.12rem 0.45rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      white-space: nowrap;
      width: fit-content;
    }

    .status-badge.expired {
      border-color: #f2be7a;
      background: #fff7ec;
      color: #ad5a00;
    }

    .verified-badge {
      display: inline-block;
      font-size: 0.7rem;
      font-weight: 600;
      color: #0097d7;
      letter-spacing: 0.01em;
    }

    .dots {
      width: 30px;
      height: 30px;
      border: 1px solid #b7dff0;
      border-radius: 999px;
      background: var(--jci-white);
      color: var(--jci-blue);
      opacity: 0;
      cursor: pointer;
      transition: opacity 0.1s;
    }

    .empty {
      text-align: center;
      color: var(--app-muted);
      padding: 1rem;
    }

    @media (max-width: 1080px) {
      .toolbar {
        flex-direction: column;
        align-items: stretch;
      }
    }
  `
})
export class MembersComponent implements OnInit {
  loading = true;
  searchTerm = '';
  filter: MemberFilter = 'active';
  firstNameFilter = '';
  lastNameFilter = '';
  emailFilter = '';
  phoneFilter = '';
  addressFilter = '';
  kotaFilter = '';
  provinsiFilter = '';
  joinedDateFilter = '';
  sidePanel: 'filter' | 'customize' | null = null;
  drawerTop = 96;
  openMenu: 'manage' | 'export' | null = null;
  nameSort: 'asc' | 'desc' = 'asc';
  columns: Record<ColumnKey, boolean> = {
    name: true,
    email: true,
    phone: true,
    status: true,
    joinedDate: true,
    address: true,
    points: false
  };
  readonly columnKeys: ColumnKey[] = ['name', 'email', 'phone', 'status', 'joinedDate', 'address', 'points'];
  selectedViewId = 'active';
  views: ViewPreset[] = [
    {
      id: 'all',
      name: 'All members',
      filter: 'all',
      sort: 'asc',
      columns: { name: true, email: true, phone: true, status: true, joinedDate: true, address: true, points: false }
    },
    {
      id: 'active',
      name: 'Active members',
      filter: 'active',
      sort: 'asc',
      columns: { name: true, email: true, phone: true, status: true, joinedDate: true, address: true, points: false }
    },
    {
      id: 'expired',
      name: 'Expired members',
      filter: 'expired',
      sort: 'asc',
      columns: { name: true, email: true, phone: true, status: true, joinedDate: true, address: true, points: false }
    }
  ];
  rows: MemberRow[] = [];

  constructor(
    private readonly directus: DirectusService,
    private readonly router: Router
  ) {}

  openProfile(id: string): void {
    this.router.navigate(['/members', id]);
  }

  async ngOnInit(): Promise<void> {
    await this.load();
    this.updateDrawerOffset();
    const defaultViewId = localStorage.getItem('members_default_view');
    if (defaultViewId && this.views.some((view) => view.id === defaultViewId)) {
      this.applyView(defaultViewId);
    }
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.updateDrawerOffset();
  }

  get filteredRows(): MemberRow[] {
    const needle = this.searchTerm.trim().toLowerCase();
    const rows = this.rows.filter((row) => {
      const byStatus =
        this.filter === 'all' ? true : this.filter === 'active' ? row.status === 'Active' : row.status === 'Expired';
      if (!byStatus) return false;
      if (needle) {
        const matchesSearch = [
          row.name,
          row.firstName,
          row.lastName,
          row.email,
          row.phone,
          row.address,
          row.addressRaw,
          row.kota,
          row.provinsi,
          row.joinedDate
        ]
          .join(' ')
          .toLowerCase()
          .includes(needle);
        if (!matchesSearch) return false;
      }
      if (this.firstNameFilter && !row.firstName.toLowerCase().includes(this.firstNameFilter.trim().toLowerCase())) return false;
      if (this.lastNameFilter && !row.lastName.toLowerCase().includes(this.lastNameFilter.trim().toLowerCase())) return false;
      if (this.emailFilter && !row.email.toLowerCase().includes(this.emailFilter.trim().toLowerCase())) return false;
      if (this.phoneFilter && !row.phone.toLowerCase().includes(this.phoneFilter.trim().toLowerCase())) return false;
      if (this.addressFilter && !row.addressRaw.toLowerCase().includes(this.addressFilter.trim().toLowerCase())) return false;
      if (this.kotaFilter && !row.kota.toLowerCase().includes(this.kotaFilter.trim().toLowerCase())) return false;
      if (this.provinsiFilter && !row.provinsi.toLowerCase().includes(this.provinsiFilter.trim().toLowerCase())) return false;
      if (this.joinedDateFilter) {
        const joinedIso = row.joinedDateRaw.slice(0, 10);
        if (joinedIso !== this.joinedDateFilter) return false;
      }
      return true;
    });
    rows.sort((a, b) => this.nameSort === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
    return rows;
  }

  get tableColspan(): number {
    return 1 + Number(this.columns.name) + Number(this.columns.email) + Number(this.columns.phone) + Number(this.columns.status) + Number(this.columns.joinedDate) + Number(this.columns.address) + Number(this.columns.points) + 1;
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.filter !== 'all') count += 1;
    if (this.firstNameFilter.trim()) count += 1;
    if (this.lastNameFilter.trim()) count += 1;
    if (this.emailFilter.trim()) count += 1;
    if (this.phoneFilter.trim()) count += 1;
    if (this.addressFilter.trim()) count += 1;
    if (this.kotaFilter.trim()) count += 1;
    if (this.provinsiFilter.trim()) count += 1;
    if (this.joinedDateFilter) count += 1;
    return count;
  }

  toggleMenu(target: 'manage' | 'export'): void {
    this.openMenu = this.openMenu === target ? null : target;
  }

  openSidePanel(panel: 'filter' | 'customize'): void {
    this.openMenu = null;
    this.updateDrawerOffset();
    this.sidePanel = this.sidePanel === panel ? null : panel;
  }

  closeSidePanel(): void {
    this.sidePanel = null;
  }

  private updateDrawerOffset(): void {
    const topBanner = document.querySelector('.top') as HTMLElement | null;
    if (!topBanner) {
      this.drawerTop = 96;
      return;
    }
    this.drawerTop = Math.max(0, Math.round(topBanner.getBoundingClientRect().bottom));
  }

  toggleNameSort(): void {
    this.nameSort = this.nameSort === 'asc' ? 'desc' : 'asc';
  }

  toggleColumn(key: ColumnKey): void {
    this.columns = { ...this.columns, [key]: !this.columns[key] };
  }

  columnLabel(key: ColumnKey): string {
    if (key === 'joinedDate') return 'Joined';
    if (key === 'status') return 'Member status';
    if (key === 'points') return 'Points';
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  applyView(id: string): void {
    const view = this.views.find((item) => item.id === id);
    if (!view) return;
    this.selectedViewId = view.id;
    this.filter = view.filter;
    this.nameSort = view.sort;
    this.columns = { ...view.columns };
  }

  countForView(viewFilter: MemberFilter): number {
    if (viewFilter === 'all') return this.rows.length;
    if (viewFilter === 'active') return this.rows.filter((row) => row.status === 'Active').length;
    return this.rows.filter((row) => row.status === 'Expired').length;
  }

  saveViewChanges(): void {
    const idx = this.views.findIndex((view) => view.id === this.selectedViewId);
    if (idx < 0) return;
    this.views[idx] = {
      ...this.views[idx],
      filter: this.filter,
      sort: this.nameSort,
      columns: { ...this.columns }
    };
    this.openMenu = null;
  }

  saveAsNewView(): void {
    const name = window.prompt('New view name');
    if (!name) return;
    const id = `view-${Date.now()}`;
    this.views = [
      ...this.views,
      {
        id,
        name: name.trim(),
        filter: this.filter,
        sort: this.nameSort,
        columns: { ...this.columns }
      }
    ];
    this.selectedViewId = id;
    this.openMenu = null;
  }

  renameView(): void {
    const idx = this.views.findIndex((view) => view.id === this.selectedViewId);
    if (idx < 0) return;
    const name = window.prompt('Rename view', this.views[idx].name);
    if (!name) return;
    this.views[idx] = { ...this.views[idx], name: name.trim() };
    this.views = [...this.views];
    this.openMenu = null;
  }

  setDefaultView(): void {
    localStorage.setItem('members_default_view', this.selectedViewId);
    this.openMenu = null;
  }

  deleteCurrentView(): void {
    if (this.selectedViewId === 'all' || this.selectedViewId === 'active' || this.selectedViewId === 'expired') {
      return;
    }
    this.views = this.views.filter((view) => view.id !== this.selectedViewId);
    this.applyView('all');
    this.openMenu = null;
  }

  clearFilters(): void {
    this.firstNameFilter = '';
    this.lastNameFilter = '';
    this.emailFilter = '';
    this.phoneFilter = '';
    this.addressFilter = '';
    this.kotaFilter = '';
    this.provinsiFilter = '';
    this.joinedDateFilter = '';
    this.filter = 'all';
  }

  exportCsv(): void {
    const csv = this.exportRows()
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    this.downloadFile(csv, 'members.csv', 'text/csv;charset=utf-8;');
    this.openMenu = null;
  }

  exportExcel(): void {
    const rows = this.exportRows();
    const header = rows[0] || [];
    const body = rows.slice(1);
    const html = [
      '<table><thead><tr>',
      ...header.map((cell) => `<th>${this.escapeHtml(cell)}</th>`),
      '</tr></thead><tbody>',
      ...body.map((row) => `<tr>${row.map((cell) => `<td>${this.escapeHtml(cell)}</td>`).join('')}</tr>`),
      '</tbody></table>'
    ].join('');
    this.downloadFile(html, 'members.xls', 'application/vnd.ms-excel');
    this.openMenu = null;
  }

  private exportRows(): string[][] {
    const headers: string[] = [];
    const pickers: Array<(row: MemberRow) => string> = [];
    this.columnKeys.forEach((key) => {
      if (!this.columns[key]) return;
      headers.push(this.columnLabel(key));
      pickers.push((row) => {
        if (key === 'name') return row.name;
        if (key === 'email') return row.email;
        if (key === 'phone') return row.phone;
        if (key === 'status') return row.status;
        if (key === 'joinedDate') return row.joinedDate;
        if (key === 'points') return String(row.point || 0);
        return row.address;
      });
    });

    const body = this.filteredRows.map((row) => pickers.map((fn) => fn(row)));
    return [headers, ...body];
  }

  private downloadFile(content: string, fileName: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const data = await this.listProfilesSafe();

      this.rows = data.map((raw) => {
        const row = raw as Record<string, unknown>;
        const first = String(row['first_name'] ?? '').trim();
        const last = String(row['last_name'] ?? '').trim();
        const name = [first, last].filter(Boolean).join(' ').trim() || 'Member';
        const status = row['subscribed'] === true ? 'Active' : 'Expired';
        const joinedSource = String(row['join_date'] ?? row['created_at'] ?? '').trim();
        const kota = String(row['address_kota'] ?? '').trim();
        const provinsi = String(row['address_provinsi'] ?? '').trim();
        const addressRaw = String(row['address'] ?? '').trim();
        const address = addressRaw || [kota, provinsi].filter(Boolean).join(', ') || '—';
        return {
          id: String(row['id'] ?? ''),
          initials: this.initials(first, last, name),
          avatar: row['avatar'] ? String(row['avatar']) : null,
          name,
          firstName: first,
          lastName: last,
          email: String(row['email'] ?? '').trim() || '—',
          phone: String(row['phone'] ?? '').trim() || '—',
          status,
          verified: row['verified'] === true,
          joinedDate: this.dateLabel(joinedSource),
          joinedDateRaw: joinedSource,
          subscribedUntil: this.dateLabel(String(row['subscribed_until'] ?? '').trim()),
          point: typeof row['point'] === 'number' ? row['point'] : 0,
          addressRaw,
          kota,
          provinsi,
          address
        } as MemberRow;
      });
    } finally {
      this.loading = false;
    }
  }

  private async listProfilesSafe(): Promise<Record<string, unknown>[]> {
    const fieldSets = [
      'id,first_name,last_name,email,phone,avatar,address,address_kota,address_provinsi,subscribed,subscribed_until,verified,join_date,created_at,point',
      'id,first_name,last_name,email,phone,address,address_kota,address_provinsi,subscribed,join_date,created_at',
      'id,first_name,last_name,email,phone,address,subscribed,created_at',
      'id,first_name,last_name,email,phone,subscribed,created_at'
    ];

    for (const fields of fieldSets) {
      try {
        return await this.directus.list('profiles', {
          fields,
          sort: '-id',
          limit: 500
        });
      } catch {
        continue;
      }
    }

    return [];
  }

  private initials(first: string, last: string, fallback: string): string {
    const a = first.charAt(0);
    const b = last.charAt(0);
    const fromNames = `${a}${b}`.trim();
    if (fromNames) return fromNames.toUpperCase();
    return fallback.slice(0, 2).toUpperCase();
  }

  private dateLabel(value: string): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }
}
